import { useState, useEffect, useCallback } from 'react';
import { signUp, signInWithGoogle, authFetch, getSession } from '../api';

// ─── Credit calculator ─────────────────────────────────────────────────────
// Shows how many calls each credit amount buys for a given build price
function creditOptions(pricePerCall) {
  return [
    { usd: 5,   calls: Math.floor(5   / pricePerCall) },
    { usd: 10,  calls: Math.floor(10  / pricePerCall) },
    { usd: 25,  calls: Math.floor(25  / pricePerCall) },
    { usd: 50,  calls: Math.floor(50  / pricePerCall) },
    { usd: 100, calls: Math.floor(100 / pricePerCall) },
  ];
}

const STEPS = ['account', 'credits', 'key'];
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

export default function GetKey({ navigate }) {
  const [step, setStep]       = useState('account');
  const [form, setForm]       = useState({ name: '', email: '', password: '' });
  const [result, setResult]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [copied, setCopied]   = useState('');
  const [topupAmount, setTopupAmount] = useState(10);
  const [toppingUp, setToppingUp]     = useState(false);
  const [topupDone, setTopupDone]     = useState(false);
  const [lang, setLang]       = useState('sdk');

  // If already signed in, redirect to account dashboard
  const existingSession = getSession();
  useEffect(() => {
    if (existingSession) navigate('consumer');
  }, [existingSession, navigate]);

  // Google OAuth for quick signup
  const handleGoogleResponse = useCallback(async (response) => {
    if (!response.credential) return;
    setLoading(true); setError('');
    try {
      const data = await signInWithGoogle(response.credential);
      if (data.token) {
        setResult({ consumerId: data.consumerId, apiKey: data.apiKey });
        if (data.newAccount && data.apiKey) { setStep('credits'); }
        else { navigate('consumer'); }
      } else { setError(data.error || 'Google sign-in failed'); }
    } catch { setError('Connection failed'); }
    setLoading(false);
  }, [navigate]);

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID || step !== 'account') return;
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.onload = () => {
      if (window.google?.accounts?.id) {
        window.google.accounts.id.initialize({ client_id: GOOGLE_CLIENT_ID, callback: handleGoogleResponse });
        const container = document.getElementById('google-signup-btn');
        if (container) {
          window.google.accounts.id.renderButton(container, { theme: 'filled_black', size: 'large', width: '380', text: 'signup_with', shape: 'rectangular' });
        }
      }
    };
    document.body.appendChild(script);
    return () => { try { document.body.removeChild(script); } catch {} };
  }, [handleGoogleResponse, step]);

  // Featured build for the example — try to load one from the marketplace
  const [featuredBuild, setFeaturedBuild] = useState({ slug: 'drc-pattern-scanner', price_per_call: 0.10 });
  useEffect(() => {
    fetch('/v1/builds').then(r => r.json()).then(d => {
      if (d.builds?.length) setFeaturedBuild(d.builds[0]);
    }).catch(() => {});
  }, []);

  async function createAccount() {
    setError('');
    if (!form.email) { setError('Email is required'); return; }
    if (!form.password || form.password.length < 8) { setError('Password must be at least 8 characters'); return; }
    setLoading(true);
    try {
      const d = await signUp({ name: form.name || form.email, email: form.email, password: form.password });
      if (d.error) { setError(d.error); }
      else { setResult(d); setStep('credits'); }
    } catch { setError('Request failed — is the server running?'); }
    setLoading(false);
  }

  async function topup() {
    setToppingUp(true); setError('');
    try {
      const r = await authFetch('/v1/billing/topup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amountUsd: topupAmount }),
      });
      const d = await r.json();
      if (d.url && d.url.startsWith('https://checkout.stripe.com')) { window.location.href = d.url; return; }
      if (d.dev_mode || d.success) { setTopupDone(true); setTimeout(() => setStep('key'), 800); }
      else setError(d.error || 'Top-up failed');
    } catch { setError('Top-up request failed'); }
    setToppingUp(false);
  }

  function copy(text, label) {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  }

  const codeSnippets = {
    sdk: `// npm install invokeapi
const InvokeAPI = require('invokeapi');
const client = new InvokeAPI('${result?.apiKey || 'am_live_your_key_here'}');

// Call a build
const result = await client.call('${featuredBuild.slug}', {
  query: 'your question here'
});
console.log(result);

// Check your balance
console.log('Balance: $' + await client.getBalance());`,

    python: `import requests

res = requests.post(
    "https://invokeapi-production.up.railway.app/v1/call/${featuredBuild.slug}",
    headers={"Authorization": "Bearer ${result?.apiKey || 'YOUR_API_KEY'}"},
    json={"query": "your question here"}
)
print(res.json()["result"]["answer"])`,

    js: `const res = await fetch(
  "https://invokeapi-production.up.railway.app/v1/call/${featuredBuild.slug}",
  {
    method: "POST",
    headers: {
      "Authorization": "Bearer ${result?.apiKey || 'YOUR_API_KEY'}",
      "Content-Type": "application/json"
    },
    body: JSON.stringify({ query: "your question here" })
  }
);
const { result } = await res.json();`,

    curl: `curl -X POST https://invokeapi-production.up.railway.app/v1/call/${featuredBuild.slug} \\
  -H "Authorization: Bearer ${result?.apiKey || 'YOUR_API_KEY'}" \\
  -H "Content-Type: application/json" \\
  -d '{"query": "your question here"}'`,
  };

  const s = {
    wrap: { maxWidth: 560 },
    // Progress bar
    progress: { display: 'flex', alignItems: 'center', gap: 0, marginBottom: 40 },
    progStep: (active, done) => ({
      display: 'flex', alignItems: 'center', gap: 8, flex: 1,
      color: done ? 'var(--green)' : active ? 'var(--text)' : 'var(--muted)',
      fontSize: 13, fontWeight: active ? 600 : 400,
    }),
    progDot: (active, done) => ({
      width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: done ? 'var(--green)' : active ? 'var(--accent)' : 'var(--bg3)',
      color: done || active ? '#fff' : 'var(--muted)',
      fontSize: 12, fontWeight: 700,
      border: `1px solid ${done ? 'var(--green)' : active ? 'var(--accent)' : 'var(--border)'}`,
    }),
    progLine: (done) => ({
      height: 1, flex: 1, background: done ? 'var(--green)' : 'var(--border)', margin: '0 8px',
    }),
    // Form
    h2: { fontSize: 22, fontWeight: 600, marginBottom: 6 },
    sub: { color: 'var(--muted)', fontSize: 14, marginBottom: 28, lineHeight: 1.5 },
    label: { display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6 },
    input: { width: '100%', padding: '11px 14px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)', fontSize: 14, outline: 'none', marginBottom: 14 },
    btn: { padding: '12px 28px', borderRadius: 'var(--radius-sm)', border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 600, fontSize: 15, cursor: 'pointer', width: '100%' },
    btnSecondary: { padding: '11px 28px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'transparent', color: 'var(--muted)', fontWeight: 500, fontSize: 14, cursor: 'pointer', width: '100%', marginTop: 10 },
    error: { color: 'var(--red)', fontSize: 13, marginBottom: 14, padding: '8px 12px', background: 'rgba(239,68,68,0.06)', borderRadius: 'var(--radius-sm)', border: '1px solid rgba(239,68,68,0.15)' },
    // Credit options
    creditGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 20 },
    creditCard: (sel) => ({
      padding: '14px 10px', borderRadius: 'var(--radius-sm)', textAlign: 'center', cursor: 'pointer',
      border: `1px solid ${sel ? 'var(--accent)' : 'var(--border)'}`,
      background: sel ? 'rgba(124,111,239,0.10)' : 'var(--bg2)',
    }),
    creditAmt: { fontWeight: 700, fontSize: 18, color: 'var(--accent2)' },
    creditCalls: { fontSize: 12, color: 'var(--muted)', marginTop: 4 },
    // Key display
    keyBox: { background: 'var(--bg)', border: '1px solid rgba(124,111,239,0.4)', borderRadius: 'var(--radius-sm)', padding: '12px 14px', fontFamily: 'monospace', fontSize: 13, wordBreak: 'break-all', lineHeight: 1.6, position: 'relative', marginBottom: 6 },
    copyBtn: { position: 'absolute', top: 8, right: 8, padding: '3px 10px', fontSize: 11, border: '1px solid var(--border)', borderRadius: 4, background: 'var(--bg2)', color: 'var(--muted)', cursor: 'pointer' },
    warn: { fontSize: 12, color: 'var(--muted)', padding: '8px 12px', background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)', borderRadius: 'var(--radius-sm)', marginBottom: 20 },
    idRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '8px 12px', marginBottom: 20 },
    idLabel: { fontSize: 12, color: 'var(--muted)' },
    idVal: { fontFamily: 'monospace', fontSize: 11, color: 'var(--muted)' },
    idCopy: { padding: '3px 10px', fontSize: 11, border: '1px solid var(--border)', borderRadius: 4, background: 'transparent', color: 'var(--muted)', cursor: 'pointer' },
    // Code
    langTabs: { display: 'flex', gap: 6, marginBottom: 10 },
    langTab: (active) => ({ padding: '5px 14px', borderRadius: 'var(--radius-sm)', fontSize: 12, fontWeight: 500, border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`, background: active ? 'rgba(124,111,239,0.12)' : 'transparent', color: active ? 'var(--accent2)' : 'var(--muted)', cursor: 'pointer' }),
    codeBlock: { background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '14px 16px', fontFamily: 'monospace', fontSize: 12, lineHeight: 1.7, color: 'var(--accent2)', whiteSpace: 'pre', overflowX: 'auto', position: 'relative', marginBottom: 20 },
    successDot: { display: 'inline-flex', alignItems: 'center', gap: 8, color: 'var(--green)', fontWeight: 500, fontSize: 14 },
  };

  const credits = creditOptions(featuredBuild.price_per_call);
  const stepDefs = [
    { id: 'account', label: 'Create account' },
    { id: 'credits', label: 'Add credits' },
    { id: 'key',     label: 'Start calling' },
  ];

  return (
    <div style={s.wrap}>
      {/* Progress indicator */}
      <div style={s.progress}>
        {stepDefs.map((sd, i) => {
          const currentIdx = stepDefs.findIndex(x => x.id === step);
          const isDone   = i < currentIdx;
          const isActive = i === currentIdx;
          return (
            <div key={sd.id} style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
              <div style={s.progStep(isActive, isDone)}>
                <div style={s.progDot(isActive, isDone)}>
                  {isDone ? '✓' : i + 1}
                </div>
                <span>{sd.label}</span>
              </div>
              {i < stepDefs.length - 1 && <div style={s.progLine(isDone)} />}
            </div>
          );
        })}
      </div>

      {/* ── Step 1: Create account ──────────────────────────────────── */}
      {step === 'account' && (
        <div>
          <div style={s.h2}>Create your account</div>
          <div style={s.sub}>Get an API key that works across every build in the marketplace.</div>

          {GOOGLE_CLIENT_ID && (
            <>
              <div id="google-signup-btn" style={{ marginBottom: 4 }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '16px 0', color: 'var(--muted)', fontSize: 13 }}>
                <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
                <span>or</span>
                <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
              </div>
            </>
          )}

          <label style={s.label}>Name</label>
          <input style={s.input} value={form.name}
                 onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                 placeholder="Your name" />

          <label style={s.label}>Email *</label>
          <input style={s.input} type="email" value={form.email}
                 onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                 placeholder="you@example.com" />

          <label style={s.label}>Password *</label>
          <input style={s.input} type="password" value={form.password}
                 onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                 placeholder="Min 6 characters"
                 onKeyDown={e => e.key === 'Enter' && createAccount()} />

          {error && <div style={s.error}>{error}</div>}
          <button style={s.btn} onClick={createAccount} disabled={loading}>
            {loading ? 'Creating...' : 'Continue →'}
          </button>

          <p style={{ textAlign: 'center', fontSize: 13, color: 'var(--muted)', marginTop: 16 }}>
            Already have an account?{' '}
            <span style={{ color: 'var(--accent2)', cursor: 'pointer', fontWeight: 500 }} onClick={() => navigate('signin')}>Sign in</span>
          </p>

          {/* Quick explainer */}
          <div style={{ marginTop: 32, borderTop: '1px solid var(--border)', paddingTop: 24 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 16 }}>How credits work</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[
                { icon: '💳', t: 'Buy credits once', d: '$5, $10, $25, $50 or $100. Never expire.' },
                { icon: '📡', t: 'Call any build',   d: `Each call deducts the build's price. DRC Scanner = $${featuredBuild.price_per_call}/call.` },
                { icon: '🔄', t: 'Top up anytime',  d: 'Add more credits whenever you need them. No subscription.' },
              ].map(item => (
                <div key={item.t} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  <span style={{ fontSize: 20 }}>{item.icon}</span>
                  <div>
                    <div style={{ fontWeight: 500, fontSize: 13 }}>{item.t}</div>
                    <div style={{ fontSize: 12, color: 'var(--muted)' }}>{item.d}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Step 2: Add credits ─────────────────────────────────────── */}
      {step === 'credits' && (
        <div>
          <div style={s.h2}>Add credits</div>
          <div style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.2)', borderRadius: 'var(--radius-sm)', padding: '12px 16px', marginBottom: 16, fontSize: 13, color: 'var(--green)' }}>
            You got <strong>$0.50 free starter credits</strong> — enough to try any build right now. Add more when you're ready.
          </div>
          <div style={s.sub}>
            Pick how many credits to add. Each credit = $1 of API calls.
            Credits never expire.
          </div>

          {/* Credit cards showing call count */}
          <div style={s.creditGrid}>
            {credits.map(c => (
              <div key={c.usd} style={s.creditCard(topupAmount === c.usd)}
                   onClick={() => setTopupAmount(c.usd)}>
                <div style={s.creditAmt}>${c.usd}</div>
                <div style={s.creditCalls}>
                  ~{c.calls.toLocaleString()} calls
                </div>
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
                  on {featuredBuild.slug?.split('-').slice(0, 2).join(' ')}
                </div>
              </div>
            ))}
          </div>

          <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 16 }}>
            Selected: <strong style={{ color: 'var(--text)' }}>${topupAmount}</strong>
            {' '}≈ <strong style={{ color: 'var(--accent2)' }}>
              {Math.floor(topupAmount / featuredBuild.price_per_call).toLocaleString()} calls
            </strong> on this build
          </div>

          {error && <div style={s.error}>{error}</div>}

          {topupDone ? (
            <div style={s.successDot}>
              <span style={{ width: 24, height: 24, borderRadius: '50%', background: 'var(--green)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12 }}>✓</span>
              ${topupAmount} credits added — continuing...
            </div>
          ) : (
            <>
              <button style={s.btn} onClick={topup} disabled={toppingUp}>
                {toppingUp ? 'Redirecting to checkout...' : `Add $${topupAmount} credits →`}
              </button>
              <button style={s.btnSecondary} onClick={() => setStep('key')}>
                Skip for now — I'll add credits later
              </button>
            </>
          )}
        </div>
      )}

      {/* ── Step 3: Your key + quick start ──────────────────────────── */}
      {step === 'key' && result && (
        <div>
          <div style={s.h2}>You're ready to call builds</div>
          <div style={s.sub}>
            Save your API key — it's shown <strong>once only</strong>.
            Then copy the snippet below and start calling.
          </div>

          {/* API Key */}
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>Your API key</div>
          <div style={s.keyBox}>
            {result.apiKey}
            <button style={s.copyBtn} onClick={() => copy(result.apiKey, 'key')}>
              {copied === 'key' ? '✓' : 'copy'}
            </button>
          </div>
          <div style={s.warn}>⚠ Copy this now. We store a hash — we can never show it again.</div>

          {/* Account ID — small, not scary */}
          <div style={s.idRow}>
            <div>
              <div style={s.idLabel}>Account ID (for dashboard)</div>
              <div style={s.idVal}>{result.consumerId}</div>
            </div>
            <button style={s.idCopy} onClick={() => copy(result.consumerId, 'id')}>
              {copied === 'id' ? '✓' : 'copy'}
            </button>
          </div>

          {/* Code snippet */}
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 10 }}>Quick start</div>
          <div style={s.langTabs}>
            {['sdk', 'python', 'js', 'curl'].map(l => (
              <button key={l} style={s.langTab(lang === l)} onClick={() => setLang(l)}>{l === 'sdk' ? 'SDK' : l === 'js' ? 'JavaScript' : l.charAt(0).toUpperCase() + l.slice(1)}</button>
            ))}
          </div>
          <div style={s.codeBlock}>
            {codeSnippets[lang]}
            <button style={s.copyBtn} onClick={() => copy(codeSnippets[lang], 'code')}>
              {copied === 'code' ? '✓' : 'copy'}
            </button>
          </div>

          {/* Actions */}
          <button style={s.btn} onClick={() => navigate('marketplace')}>
            Browse builds →
          </button>
          <button style={s.btnSecondary} onClick={() => navigate('consumer')}>
            Manage account & credits
          </button>
        </div>
      )}
    </div>
  );
}
